import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { AutoComplete } from 'primeng/autocomplete';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { forkJoin, catchError, of } from 'rxjs';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { ClientService } from '../../services/client.service';
import { RoleService } from '../../services/role.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { CostCenterService } from '../../services/cost-center.service';
import { AuthService, UserInfo } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { Project } from '../../models/project.model';
import { Employee, EmployeeRole } from '../../models/employee.model';
import { Client } from '../../models/client.model';
import { Role } from '../../models/role.model';
import { ServiceGroup } from '../../models/service-group.model';
import { CostCenter } from '../../models/cost-center.model';
import { InvoiceType } from '../../models/allocation.model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-project-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    
    Dialog,
    Select,
    InputText,
    DatePicker,
    AutoComplete,
    Checkbox,
    Tooltip,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './project-management.component.html',
  styleUrl: './project-management.component.scss'
})
export class ProjectManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  projects: Project[] = [];
  filteredProjects: Project[] = [];

  // ================= View mode =================
  /** Table (default, full CRUD) or a progress-grouped Kanban board. */
  viewMode: 'table' | 'kanban' = 'table';

  /**
   * Delivery progress states, in workflow order. Defined here rather than reused
   * from a filter list because this page has no Progress filter — the board is
   * the only thing that surfaces the field.
   */
  private readonly progressStates = [
    'To start', 'On track', 'Off track', 'Blocker', 'On Hold', 'Closed',
  ];

  kanbanColumns: { key: string; label: string; cssClass: string; projects: Project[] }[] = [];

  /**
   * Columns rendered on the board. Every progress state is always shown — the
   * gaps are informative, and a board with a stable shape is easier to read.
   */
  get visibleKanbanColumns() {
    return this.kanbanColumns;
  }
  employees: Employee[] = [];
  projectManagers: Employee[] = [];
  clients: Client[] = [];
  filteredClients: Client[] = [];
  roles: Role[] = [];
  accountManagers: Employee[] = [];
  serviceGroups: ServiceGroup[] = [];
  costCenters: CostCenter[] = [];
  filteredCostCenters: CostCenter[] = [];
  
  // Expose InvoiceType enum to template
  InvoiceType = InvoiceType;

  loading: boolean = true;
  displayDialog: boolean = false;
  isEditMode: boolean = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active'; // 'all', 'active', 'inactive' - default to 'active'
  
  // Filter properties
  selectedProjectManagerFilter: string | null = null;
  selectedClientFilter: string | null = null;
  selectedAccountManagerFilter: string | null = null;
  selectedServiceGroupFilter: string | null = null;
  
  // CostCenter autocomplete
  costCenterSearchText: string = '';
  selectedCostCenter: CostCenter | null = null;

  // Client autocomplete (same pattern as CostCenter — select existing or type new)
  clientSearchText: string = '';
  selectedClient: Client | null = null;
  
  canAddProject: boolean = false;

  // Pagination properties
  rows: number = 50; // Default rows per page
  first: number = 0; // First row index
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  fieldTouched: Record<string, boolean> = {};

  selectedProject: Project = this.getEmptyProject();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  
  // Current user context
  user: UserInfo | null = null;
  currentUserEmployee: Employee | null = null;

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  invoiceTypeOptions: { label: string; value: InvoiceType }[] = [
    { label: 'Monthly', value: InvoiceType.Monthly },
    { label: 'Project Milestone', value: InvoiceType.ProjectMilestone },
    { label: 'N/A', value: InvoiceType.NotAvailable }
  ];

  constructor(
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private clientService: ClientService,
    private roleService: RoleService,
    private serviceGroupService: ServiceGroupService,
    private costCenterService: CostCenterService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService,
    private route: ActivatedRoute
  ) {
    // Handoff from the Project Details dialog's "Open in Inventory":
    // /projects-inventory?projectId=…&openForm=1. Read in the constructor so the flags are
    // set before the load resolves and tries to consume them.
    const qp = this.route.snapshot.queryParamMap;
    this.pendingProjectId = qp.get('projectId');
    this.pendingOpenForm = qp.get('openForm') === '1';
  }

  /** Set by the ?projectId=…&openForm=1 handoff; consumed once after the first load. */
  private pendingProjectId: string | null = null;
  private pendingOpenForm = false;

  /**
   * Completes the handoff: opens the edit dialog on the requested project.
   *
   * Deferred until the projects have loaded, because the dialog is populated from the
   * loaded row rather than re-fetched. Consumed once and cleared either way, so a later
   * filter change cannot reopen a stale project.
   */
  private consumePendingProjectHandoff(): void {
    if (!this.pendingOpenForm || !this.pendingProjectId) return;

    const projectId = this.pendingProjectId;
    this.pendingOpenForm = false;
    this.pendingProjectId = null;

    const project = this.projects.find(p => p.id === projectId);
    if (!project) {
      // Deleted or out of this user's scope between the two screens. Saying so beats
      // opening nothing and looking like a dead click.
      this.messageService.add({
        severity: 'warn',
        summary: 'Project unavailable',
        detail: 'That project could not be found.'
      });
      return;
    }

    this.showEditDialog(project);
  }

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by project name or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Project');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadData();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showAddDialog();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
      this.canAddProject = user?.isAdmin || user?.isPM || false;
      this.pageHeaderService.setCanShowPrimaryAction(this.canAddProject);
      this.applySearch();
    });
  }

  ngOnDestroy() {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;
    
    // Use forkJoin instead of Promise.all for better RxJS integration
    // This allows better error handling - each request handles errors independently
    forkJoin({
      projects: this.projectService.getAllProjects().pipe(
        catchError(error => {
          console.error('Error loading projects:', error);
          return of([] as Project[]);
        })
      ),
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          console.error('Error loading employees:', error);
          return of([] as Employee[]);
        })
      ),
      clients: this.clientService.getAllClients().pipe(
        catchError(error => {
          console.error('Error loading clients:', error);
          return of([] as Client[]);
        })
      ),
      roles: this.roleService.getAllRoles().pipe(
        catchError(error => {
          console.error('Error loading roles:', error);
          return of([] as Role[]);
        })
      ),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          console.error('Error loading service groups:', error);
          return of([] as ServiceGroup[]);
        })
      ),
      costCenters: this.costCenterService.getAllCostCenters().pipe(
        catchError(error => {
          console.error('Error loading cost centers:', error);
          return of([] as CostCenter[]);
        })
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.projects = data.projects || [];
        this.employees = data.employees || [];
        this.clients = (data.clients || []).filter(c => c.isActive);
        this.filteredClients = [...this.clients];
        this.roles = (data.roles || []).filter(r => r.isActive);
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        this.costCenters = (data.costCenters || []).filter(cc => cc.isActive);
        this.filteredCostCenters = [...this.costCenters];
        
        // Resolve current user's employee record by email for PM group filtering
        if (this.user?.email) {
          this.currentUserEmployee = this.employees.find(
            e => e.email?.toLowerCase() === this.user!.email.toLowerCase()
          ) || null;
        } else {
          this.currentUserEmployee = null;
        }
        
        // Project Manager dropdown: employees who have PM role in roleIds or as primary roleId
        const pmRole = this.roles.find(r =>
          r.title?.toLowerCase().includes('project manager') || r.title?.toLowerCase() === 'pm'
        );
        this.projectManagers = pmRole
          ? this.employees.filter(e => e.isActive && (
            (e.roleIds && e.roleIds.includes(pmRole.id)) || e.roleId === pmRole.id
          ))
          : [];

        // Account Manager dropdown: employees who have Account Manager role in roleIds or as primary roleId
        const accountManagerRole = this.roles.find(r => r.title?.toLowerCase() === 'account manager');
        if (accountManagerRole) {
          this.accountManagers = this.employees.filter(e =>
            e.isActive && (
              (e.roleIds && e.roleIds.includes(accountManagerRole.id)) || e.roleId === accountManagerRole.id
            )
          );
        } else {
          this.accountManagers = [];
        }
        
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;

        // Deferred until here: the edit dialog is populated from the loaded row, and its
        // client / manager dropdowns need the lists beside it.
        this.consumePendingProjectHandoff();
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Drop the handoff rather than leaving it armed: the projects never loaded, so a
        // later successful load must not pop a dialog the user has stopped expecting.
        this.pendingOpenForm = false;
        this.pendingProjectId = null;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.projects];

    // Role-based restriction: PMs only see projects in their service group or managed by them
    if (this.user && !this.user.isAdmin && this.user.isPM) {
      if (this.currentUserEmployee) {
        const pmEmployeeId = this.currentUserEmployee.id;
        filtered = filtered.filter(p => p.managerId === pmEmployeeId);
      } else {
        filtered = [];
      }
    }

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(proj =>
        proj.name?.toLowerCase().includes(searchLower) ||
        proj.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(proj => proj.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(proj => proj.isActive === false);
      }
    }

    // Apply Project Manager filter
    if (this.selectedProjectManagerFilter) {
      filtered = filtered.filter(proj => proj.managerId === this.selectedProjectManagerFilter);
    }

    // Apply Client filter
    if (this.selectedClientFilter) {
      filtered = filtered.filter(proj => proj.clientId === this.selectedClientFilter);
    }

    // Apply Account Manager filter
    if (this.selectedAccountManagerFilter) {
      filtered = filtered.filter(proj => proj.accountManagerId === this.selectedAccountManagerFilter);
    }

    // Apply Service Group filter
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(proj => proj.serviceGroupId === this.selectedServiceGroupFilter);
    }

    this.filteredProjects = filtered;
    this.totalRecords = this.filteredProjects.length;
    this.first = 0;
    if (this.viewMode === 'kanban') this.rebuildKanbanColumns();
    this.pageHeaderService.setExportDisabled(this.filteredProjects.length === 0);
  }

  setViewMode(mode: 'table' | 'kanban'): void {
    this.viewMode = mode;
    if (mode === 'kanban') this.rebuildKanbanColumns();
  }

  /**
   * Buckets the filtered projects into one column per progress state. Anything
   * with no progress set (or an unrecognised legacy value) lands in "Not set"
   * rather than disappearing from the board.
   */
  private rebuildKanbanColumns(): void {
    const columns = this.progressStates.map(s => ({
      key: s,
      label: s,
      cssClass: 'progress-' + s.toLowerCase().replace(/\s+/g, '-'),
      projects: [] as Project[],
    }));
    const unset = { key: '', label: 'Not set', cssClass: 'progress-unset', projects: [] as Project[] };

    for (const p of this.filteredProjects) {
      const col = columns.find(c => c.key.toLowerCase() === (p.progress ?? '').toLowerCase());
      (col ?? unset).projects.push(p);
    }
    // Only offer the catch-all column when something actually needs it.
    this.kanbanColumns = unset.projects.length > 0 ? [...columns, unset] : columns;
  }

  /** RAG severity for a card's left edge, mirroring the Project List board. */
  getStatusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'success';
      case 'amber': case 'yellow': return 'warn';
      case 'red': return 'danger';
      default: return 'secondary';
    }
  }

  /** Pill class for the RAG badge — shared rules in styles/_badges.scss. */
  getStatusBadgeClass(status?: string): string {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'status-badge-green';
      case 'amber': case 'yellow': return 'status-badge-amber';
      case 'red': return 'status-badge-red';
      default: return 'status-badge-default';
    }
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  /** Stats for cards (from full projects list). */
  get totalProjectsCount(): number {
    return this.projects.length;
  }
  get activeProjectsCount(): number {
    return this.projects.filter(p => p.isActive).length;
  }
  get inactiveProjectsCount(): number {
    return this.projects.filter(p => !p.isActive).length;
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedStatusFilter !== 'active' ||
      this.selectedProjectManagerFilter ||
      this.selectedClientFilter ||
      this.selectedAccountManagerFilter ||
      this.selectedServiceGroupFilter
    );
  }

  clearFilters() {
    this.searchText = '';
    this.selectedStatusFilter = 'active'; // Reset to default 'active' view
    this.selectedProjectManagerFilter = null;
    this.selectedClientFilter = null;
    this.selectedAccountManagerFilter = null;
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  showAddDialog() {
    this.selectedProject = this.getEmptyProject();
    
    // Initialize start date from the project's startDate string
    if (this.selectedProject.startDate) {
      const startDateParts = this.selectedProject.startDate.split('-');
      if (startDateParts.length === 3) {
        this.selectedStartDate = new Date(parseInt(startDateParts[0]), parseInt(startDateParts[1]) - 1, parseInt(startDateParts[2]));
      } else {
        this.selectedStartDate = new Date(this.selectedProject.startDate);
      }
    } else {
      this.selectedStartDate = null;
    }
    
    this.selectedEndDate = null;
    this.selectedCostCenter = null;
    this.costCenterSearchText = '';
    this.selectedClient = null;
    this.clientSearchText = '';
    this.fieldTouched = {};
    this.isEditMode = false;
    this.displayDialog = true;
  }

  showEditDialog(project: Project) {
    // Convert date strings to Date objects for the date picker
    this.selectedProject = { ...project };
    
    // Convert start date string to Date object
    if (project.startDate) {
      const startDateParts = project.startDate.split('-');
      if (startDateParts.length === 3) {
        this.selectedStartDate = new Date(parseInt(startDateParts[0]), parseInt(startDateParts[1]) - 1, parseInt(startDateParts[2]));
      } else {
        this.selectedStartDate = new Date(project.startDate);
      }
    } else {
      this.selectedStartDate = null;
    }
    
    // Convert end date string to Date object
    if (project.endDate) {
      const endDateParts = project.endDate.split('-');
      if (endDateParts.length === 3) {
        this.selectedEndDate = new Date(parseInt(endDateParts[0]), parseInt(endDateParts[1]) - 1, parseInt(endDateParts[2]));
      } else {
        this.selectedEndDate = new Date(project.endDate);
      }
    } else {
      this.selectedEndDate = null;
    }
    
    // Set selected cost center if exists (use DB name when lookup by id fails, e.g. after restore)
    if (project.costCenterId) {
      this.selectedCostCenter = this.costCenters.find(cc => cc.id === project.costCenterId) || null;
      this.costCenterSearchText = this.selectedCostCenter?.name || project.costCenterName || '';
    } else {
      this.selectedCostCenter = null;
      this.costCenterSearchText = '';
    }

    // Set selected client if exists (fallback to name when id lookup misses)
    if (project.clientId) {
      this.selectedClient = this.clients.find(c => c.id === project.clientId) || null;
      this.clientSearchText = this.selectedClient?.name || this.getClientName(project.clientId) || '';
    } else {
      this.selectedClient = null;
      this.clientSearchText = '';
    }

    this.fieldTouched = {};
    this.isEditMode = true;
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedCostCenter = null;
    this.costCenterSearchText = '';
    this.selectedClient = null;
    this.clientSearchText = '';
    this.fieldTouched = {};
  }

  resetProjectForm() {
    this.selectedProject = this.getEmptyProject();
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedCostCenter = null;
    this.costCenterSearchText = '';
    this.selectedClient = null;
    this.clientSearchText = '';
    this.fieldTouched = {};
  }

  isProjectFormValid(): boolean {
    return !!(
      this.selectedProject.name.trim() &&
      this.selectedProject.managerId &&
      this.selectedProject.invoiceType !== undefined && this.selectedProject.invoiceType !== null
    );
  }

  saveProject() {
    // Mark all required fields as touched
    this.fieldTouched['name'] = true;
    this.fieldTouched['managerId'] = true;
    this.fieldTouched['invoiceType'] = true;

    if (!this.isProjectFormValid()) {
      return;
    }

    // Convert Date objects back to string format (YYYY-MM-DD)
    // Both edit and add modes can change start date
    if (this.selectedStartDate) {
      const year = this.selectedStartDate.getFullYear();
      const month = String(this.selectedStartDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.selectedStartDate.getDate()).padStart(2, '0');
      this.selectedProject.startDate = `${year}-${month}-${day}`;
    } else {
      // If no start date is selected, ensure we have a valid one
      // In edit mode, keep the original if available
      if (!this.selectedProject.startDate) {
        // Fallback: set today's date if no date is available
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        this.selectedProject.startDate = `${year}-${month}-${day}`;
      }
    }

    if (this.selectedEndDate) {
      const year = this.selectedEndDate.getFullYear();
      const month = String(this.selectedEndDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.selectedEndDate.getDate()).padStart(2, '0');
      this.selectedProject.endDate = `${year}-${month}-${day}`;
    } else {
      this.selectedProject.endDate = undefined;
    }

    // Validate end date is after start date if provided
    if (this.selectedProject.endDate && this.selectedProject.startDate) {
      const startDate = new Date(this.selectedProject.startDate);
      const endDate = new Date(this.selectedProject.endDate);
      if (endDate < startDate) {
        this.fieldTouched['endDate'] = true;
        return;
      }
    }

    // Resolve client → cost center → save (chained so new entities persist before the project references them)
    this.resolveClientThenCostCenter();
  }

  private resolveClientThenCostCenter() {
    const typed = (this.clientSearchText ?? '').trim();

    if (!typed) {
      this.selectedProject.clientId = undefined;
      this.resolveCostCenterThenSave();
      return;
    }

    // Existing client selected from dropdown
    if (this.selectedClient && this.selectedClient.name === typed) {
      this.selectedProject.clientId = this.selectedClient.id;
      this.resolveCostCenterThenSave();
      return;
    }

    // Typed name matches an existing client (case-insensitive) — reuse it
    const existing = this.clients.find(c => c.name.toLowerCase() === typed.toLowerCase());
    if (existing) {
      this.selectedProject.clientId = existing.id;
      this.resolveCostCenterThenSave();
      return;
    }

    // Create new client on the fly (same UX as cost center)
    const newClient: Client = {
      id: '',
      name: typed,
      description: '',
      isActive: true,
      serviceGroupId: null,
      serviceGroupName: null,
      disableMsaNotifications: false
    };

    this.clientService.addClient(newClient).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.selectedProject.clientId = created.id;
        this.clients.push(created);
        this.filteredClients = [...this.clients];
        this.resolveCostCenterThenSave();
      },
      error: (error) => {
        console.error('Error creating client:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create client. Please try again.'
        });
      }
    });
  }

  private resolveCostCenterThenSave() {
    // Handle CostCenter - if a new one was typed, create it first
    if (this.costCenterSearchText && this.costCenterSearchText.trim() !== '') {
      if (this.selectedCostCenter && this.selectedCostCenter.name === this.costCenterSearchText.trim()) {
        // Existing cost center selected
        this.selectedProject.costCenterId = this.selectedCostCenter.id;
      } else {
        // New cost center - create it
        const newCostCenter: CostCenter = {
          id: '',
          name: this.costCenterSearchText.trim(),
          description: '',
          isActive: true,
          serviceGroupId: null,
          serviceGroupName: null
        };

        this.costCenterService.addCostCenter(newCostCenter).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (createdCostCenter) => {
            this.selectedProject.costCenterId = createdCostCenter.id;
            this.costCenters.push(createdCostCenter);
            this.filteredCostCenters = [...this.costCenters];
            this.saveProjectFinal();
          },
          error: (error) => {
            console.error('Error creating cost center:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to create cost center. Please try again.'
            });
          }
        });
        return; // Exit early, will continue after cost center is created
      }
    } else {
      this.selectedProject.costCenterId = undefined;
    }

    this.saveProjectFinal();
  }

  private saveProjectFinal() {
    if (this.isEditMode) {
      this.projectService.updateProject(this.selectedProject.id, this.selectedProject).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updated) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Project updated successfully'
          });
          const index = this.projects.findIndex(p => p.id === updated.id);
          if (index !== -1) {
            this.projects = [...this.projects];
            this.projects[index] = updated;
          }
          this.applySearch();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating project:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update project'
          });
        }
      });
    } else {
      this.projectService.addProject(this.selectedProject).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (created) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Project added successfully'
          });
          this.projects = [...this.projects, created];
          this.applySearch();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding project:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add project'
          });
        }
      });
    }
  }

  // CostCenter autocomplete methods
  searchCostCenter(event: any) {
    const query = event.query.toLowerCase();
    this.filteredCostCenters = this.costCenters.filter(cc =>
      cc.name.toLowerCase().includes(query)
    );
  }

  onCostCenterSelect(event: any) {
    const costCenter = event?.value != null ? event.value : event;
    if (!costCenter || !costCenter.id) return;
    this.selectedCostCenter = costCenter;
    this.costCenterSearchText = costCenter.name ?? '';
    this.selectedProject.costCenterId = costCenter.id;
  }

  onCostCenterClear() {
    this.selectedCostCenter = null;
    this.costCenterSearchText = '';
    this.selectedProject.costCenterId = undefined;
  }

  // Client autocomplete methods (same pattern as cost center: select existing or type new)
  searchClient(event: any) {
    const query = (event?.query ?? '').toLowerCase();
    this.filteredClients = this.clients.filter(c => c.name?.toLowerCase().includes(query));
  }

  onClientSelect(event: any) {
    const client = event?.value != null ? event.value : event;
    if (!client || !client.id) return;
    this.selectedClient = client;
    this.clientSearchText = client.name ?? '';
    this.selectedProject.clientId = client.id;
  }

  onClientClear() {
    this.selectedClient = null;
    this.clientSearchText = '';
    this.selectedProject.clientId = undefined;
  }

  toggleActiveStatus(project: Project) {
    const activating = !project.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${project.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
    const updatedProject = { ...project, isActive: !project.isActive };
    this.projectService.updateProject(project.id, updatedProject).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Project ${updatedProject.isActive ? 'activated' : 'deactivated'} successfully`
        });
        const index = this.projects.findIndex(p => p.id === project.id);
        if (index !== -1) {
          this.projects = [...this.projects];
          this.projects[index] = { ...this.projects[index], isActive: updatedProject.isActive };
        }
        this.applySearch();
      },
      error: (error) => {
        console.error('Error updating project status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update project status'
        });
      }
    });
      }
    });
  }

  getManagerName(managerId: string): string {
    const manager = this.employees.find(e => e.id === managerId);
    return manager ? manager.name : 'Unknown';
  }

  getClientName(clientId?: string): string {
    if (!clientId) return '-';
    const client = this.clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  }

  getAccountManagerName(accountManagerId?: string): string {
    if (!accountManagerId) return '-';
    const accountManager = this.accountManagers.find(e => e.id === accountManagerId);
    return accountManager ? accountManager.name : 'Unknown';
  }

  getServiceGroupName(serviceGroupId?: string): string {
    if (!serviceGroupId) return '-';
    const serviceGroup = this.serviceGroups.find(sg => sg.id === serviceGroupId);
    return serviceGroup ? serviceGroup.name : '-';
  }

  getCostCenterName(costCenterId?: string): string {
    if (!costCenterId) return '-';
    const costCenter = this.costCenters.find(cc => cc.id === costCenterId);
    return costCenter ? costCenter.name : '-';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  // ---- Kanban card helpers (shared shape with the Project List board) ----
  //
  // The delivery card carries effort where a presales card carries deal value:
  // planned vs actual days answers the same question here — is this one on
  // track against its budget.

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /** Deterministic avatar tint from a name, so a person keeps one colour. */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /**
   * Effort consumed as a percentage of the planned estimate. Null when no
   * estimate is set, so the card omits the bar rather than showing one that
   * silently means "unknown". Not capped — going over budget is what this is
   * meant to surface; the bar clamps visually, the number does not.
   */
  effortPct(project: Project): number | null {
    const planned = project.plannedEstimateDays;
    if (planned == null || planned <= 0) return null;
    const actual = project.actualEffortDays ?? 0;
    return Math.round((actual / planned) * 100);
  }

  /** Bar width, clamped to the track; the label still reports the true figure. */
  effortBarPct(project: Project): number {
    return Math.min(100, Math.max(0, this.effortPct(project) ?? 0));
  }

  /** Whole days past the end date, or null when unset / not yet due. */
  calcOverdueDays(endDate?: string | null): number | null {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return null;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - end.getTime()) / 86400000);
    return days > 0 ? days : null;
  }

  /** Days until the end date — negative once past, null when unset. */
  private daysToEnd(iso?: string | null): number | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
  }

  /** "Ends in 5d" / "Ends today", falling back to the date when far out. */
  endLabel(iso?: string | null): string {
    const d = this.daysToEnd(iso);
    if (d == null) return '';
    if (d === 0) return 'Ends today';
    if (d > 0)   return d > 60 ? this.shortDate(iso) : `Ends in ${d}d`;
    return this.shortDate(iso);
  }

  /** True when the end date is inside the next 14 days. */
  isEndingSoon(iso?: string | null): boolean {
    const d = this.daysToEnd(iso);
    return d != null && d >= 0 && d <= 14;
  }

  /** Compact date for a card foot, which shares its line with the owner. */
  shortDate(iso?: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  getInvoiceTypeName(invoiceType?: InvoiceType): string {
    if (invoiceType === undefined || invoiceType === null) {
      return '-';
    }
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'Monthly';
      case InvoiceType.ProjectMilestone:
        return 'Project Milestone';
      case InvoiceType.NotAvailable:
        return 'N/A';
      default:
        return '-';
    }
  }

  /** Same severity mapping as allocation for shared invoice-type-badge look (Monthly=success, ProjectMilestone=warn, N/A=info). */
  getInvoiceTypeSeverity(invoiceType?: InvoiceType): 'success' | 'info' | 'warn' {
    if (invoiceType === undefined || invoiceType === null) {
      return 'info';
    }
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'success';
      case InvoiceType.ProjectMilestone:
        return 'warn';
      case InvoiceType.NotAvailable:
        return 'info';
      default:
        return 'info';
    }
  }

  exportToExcel(): void {
    if (!this.filteredProjects || this.filteredProjects.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No projects to export'
      });
      return;
    }
    try {
      const exportData = this.filteredProjects.map(p => ({
        'Project Name': p.name || '',
        'Project Code': p.projectCode || '',
        'Project Manager': this.getManagerName(p.managerId),
        'Account Manager': this.getAccountManagerName(p.accountManagerId),
        'Invoicing Type': this.getInvoiceTypeName(p.invoiceType),
        'Client': this.getClientName(p.clientId),
        'Service Group': this.getServiceGroupName(p.serviceGroupId),
        'Description': (p.description || '').slice(0, 200),
        'Start Date': this.formatDate(p.startDate),
        'End Date': this.formatDate(p.endDate),
        'Status': p.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      const colWidths = [
        { wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 },
        { wch: 20 }, { wch: 16 }, { wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 10 }
      ];
      worksheet['!cols'] = colWidths;
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Projects': worksheet },
        SheetNames: ['Projects']
      };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(data, `Projects_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Projects exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting projects:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export projects to Excel'
      });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  private getEmptyProject(): Project {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    return {
      id: '',
      name: '',
      projectCode: undefined,
      managerId: '',
      invoiceType: undefined,
      clientId: undefined,
      accountManagerId: undefined,
      serviceGroupId: undefined,
      costCenterId: undefined,
      description: '',
      startDate: dateStr,
      endDate: undefined,
      isActive: true,
      isAvailableToAllEmployees: false
    };
  }
}

